// Panneau des amis (planque) : recherche de joueurs, demandes, amis avec leur présence, groupe et invitations.
// L'état vient du serveur (message « social ») ; chaque action renvoie un message, l'état revient tout seul.
import type { ClientMsg, FriendView, Presence, SocialView } from '../shared/protocol';

const $ = (id: string) => document.getElementById(id)!;
const esc = (t: string) => t.replace(/[<>&"]/g, c => `&#${c.charCodeAt(0)};`);
const STATUS: Record<Presence, string> = { offline: 'Hors ligne', hideout: 'En planque', queue: 'Cherche un raid', raid: 'En raid' };

export class SocialPanel {
  state: SocialView | null = null;
  private results: FriendView[] = [];
  private searchTimer = 0;
  onChange: (s: SocialView) => void = () => {};

  constructor(private send: (m: ClientMsg) => void) {
    $('hoFriends').onclick = () => this.toggle();
    $('socClose').onclick = () => this.toggle(false);
    const input = $('socSearch') as HTMLInputElement;
    input.oninput = () => {
      clearTimeout(this.searchTimer);
      const q = input.value.trim();
      if (q.length < 2) { this.results = []; this.render(); return; }
      this.searchTimer = window.setTimeout(() => this.send({ t: 'friend', act: 'search', q }), 250);
    };
    // Toutes les actions passent par data-a (action) et data-p (identifiant public du joueur)
    $('social').onclick = e => {
      const b = (e.target as HTMLElement).closest('[data-a]') as HTMLElement | null;
      if (!b) return;
      const pid = b.dataset.p ?? '', a = b.dataset.a!;
      if (a === 'add') this.send({ t: 'friend', act: 'request', pid });
      else if (a === 'accept' || a === 'decline' || a === 'remove') this.send({ t: 'friend', act: a, pid });
      else if (a === 'invite') this.send({ t: 'group', act: 'invite', pid });
      else if (a === 'join') this.send({ t: 'group', act: 'accept', pid });
      else if (a === 'refuse') this.send({ t: 'group', act: 'decline', pid });
      else if (a === 'kick') this.send({ t: 'group', act: 'kick', pid });
      else if (a === 'leave') this.send({ t: 'group', act: 'leave', pid: '' });
      if (a === 'add') { this.results = this.results.filter(r => r.pid !== pid); this.render(); }
    };
    addEventListener('keydown', e => { if (e.code === 'Escape' && !$('social').classList.contains('hidden')) this.toggle(false); });
  }

  toggle(on = $('social').classList.contains('hidden')) { $('social').classList.toggle('hidden', !on); if (on) this.render(); }

  set(s: SocialView) { this.state = s; this.render(); this.onChange(s); }
  searchResults(r: FriendView[]) { this.results = r; this.render(); }

  // Pastille : demandes d'ami et invitations de groupe en attente
  private badge() {
    const n = (this.state?.incoming.length ?? 0) + (this.state?.invites.length ?? 0);
    $('frBadge').textContent = String(n);
    $('frBadge').classList.toggle('hidden', !n);
  }

  private render() {
    this.badge();
    const s = this.state;
    if (!s || $('social').classList.contains('hidden')) return;
    const who = (f: FriendView, extra = '') => `<span class="socWho">${f.avatar ? `<img src="${esc(f.avatar)}" alt="">` : '<i class="socAva"></i>'}
      <span><b>${esc(f.name)}</b><small><i class="dot ${f.status}"></i>${extra || `${STATUS[f.status]} · niv. ${f.level}`}</small></span></span>`;
    const friends = new Set(s.friends.map(f => f.pid)), pending = new Set([...s.outgoing, ...s.incoming].map(f => f.pid));
    const g = s.group, lead = !g || g.leader === s.me, inGroup = new Set([...(g?.members ?? []), ...(g?.invited ?? [])].map(f => f.pid));
    const full = (g?.members.length ?? 1) + (g?.invited.length ?? 0) >= 3;
    let html = '';
    // Invitations de groupe
    if (s.invites.length) html += `<h5>Invitations de groupe</h5>${s.invites.map(f => `<div class="socRow hot">${who(f, 't’invite dans son groupe')}<span class="socAct"><button class="primary sm" data-a="join" data-p="${f.pid}">Rejoindre</button><button class="ghostBtn sm" data-a="refuse" data-p="${f.pid}">Refuser</button></span></div>`).join('')}`;
    // Groupe
    if (g) {
      html += `<h5>Groupe <small>${g.members.length} / 3</small></h5>`;
      html += g.members.map(f => `<div class="socRow">${who(f, f.pid === g.leader ? 'Chef du groupe' : STATUS[f.status])}<span class="socAct">${lead && f.pid !== s.me ? `<button class="ghostBtn sm" data-a="kick" data-p="${f.pid}">Exclure</button>` : ''}</span></div>`).join('');
      html += g.invited.map(f => `<div class="socRow dim">${who(f, 'Invité, en attente…')}</div>`).join('');
      html += `<button class="ghostBtn wide" data-a="leave">Quitter le groupe</button>`;
    }
    // Demandes reçues
    if (s.incoming.length) html += `<h5>Demandes d’ami</h5>${s.incoming.map(f => `<div class="socRow hot">${who(f)}<span class="socAct"><button class="primary sm" data-a="accept" data-p="${f.pid}">Accepter</button><button class="ghostBtn sm" data-a="decline" data-p="${f.pid}">Refuser</button></span></div>`).join('')}`;
    // Amis
    html += `<h5>Amis <small>${s.friends.filter(f => f.status !== 'offline').length} en ligne</small></h5>`;
    html += s.friends.length ? s.friends.map(f => {
      const canInvite = lead && !full && !inGroup.has(f.pid) && f.status === 'hideout';
      return `<div class="socRow ${f.status === 'offline' ? 'dim' : ''}">${who(f)}<span class="socAct">${canInvite ? `<button class="primary sm" data-a="invite" data-p="${f.pid}" title="Inviter dans ton groupe">Inviter</button>` : ''}<button class="ghostBtn sm icon" data-a="remove" data-p="${f.pid}" title="Retirer de tes amis">×</button></span></div>`;
    }).join('') : '<p class="hint">Pas encore d’amis : cherche un joueur par son nom ci-dessus.</p>';
    // Demandes envoyées
    if (s.outgoing.length) html += `<h5>Demandes envoyées</h5>${s.outgoing.map(f => `<div class="socRow dim">${who(f, 'En attente de réponse')}<span class="socAct"><button class="ghostBtn sm" data-a="decline" data-p="${f.pid}">Annuler</button></span></div>`).join('')}`;
    $('socBody').innerHTML = html;
    // Résultats de recherche
    $('socResults').innerHTML = this.results.map(f => `<div class="socRow">${who(f)}<span class="socAct">${friends.has(f.pid) ? '<small>Ami</small>' : pending.has(f.pid) ? '<small>Demande en cours</small>' : `<button class="primary sm" data-a="add" data-p="${f.pid}">Ajouter</button>`}</span></div>`).join('')
      || ((($('socSearch') as HTMLInputElement).value.trim().length >= 2) ? '<p class="hint">Aucun joueur à ce nom.</p>' : '');
  }
}
